<template>
  <section
    v-if="sectionName === 'RecipeCategoryResult'"
    class="recipe-categories result-categories__item"
  >
    <a
      v-if="hasFourImages(highlightItem)"
      :href="highlightItem.Url"
      class="mdsa-highlight category-item-box animate fadeInRight"
      @click="trackClickOnItem(searchResult, searchInput, highlightItem, sectionName, position)"
    >
      <figure>
        <div class="highlight-item-image-wrapper">
          <span
            v-for="(img, i) in highlightItem.ImageUrls"
            :key="img + i"
            :style="{ backgroundImage: 'url(' + img + ')' }"
            class="highlight-item-image"
          />
        </div>
        <figcaption>
          <h3
            class="font-text-3--bold"
            v-html="highLight(highlightItem.Header)"
          />
          <p
            class="font-text-3--light"
            v-html="highLight(highlightItem.Preamble)"
          />
        </figcaption>
      </figure>
    </a>
    <div
      v-dragscroll.x="true"
      class="mdsa-pages-wrapper"
      @dragscrollmove="dragScrollMove"
      @dragscrollend="dragScrollEnd"
    >
      <a
        v-for="(mdsa, i) in documents"
        :key="i"
        :href="mdsa.Url"
        class="mdsa-page button--secondary button--32"
        @click="checkDragStatus(searchResult, searchInput, mdsa, sectionName, position, i, $event)"
      >
        {{ mdsa.Title }}
      </a>
    </div>
  </section>

  <section
    v-else
    class="result-categories__item"
  >
    <a
      v-for="(item, i) in documents"
      :key="i"
      :style="{ animationDelay: ( i * (1 / 14)) + 's' }"
      :class="{
        'recipes-container': sectionName === 'RecipeResult',
      }"
      :href="item.Url"
      class="category-item-box animate fadeInRight"
      @click="trackClickOnItem(searchResult, searchInput, item, sectionName, position, i)"
    >
      <figure>
        <img
          v-if="item.ImageUrl"
          :src="item.ImageUrl"
          :class="{ 'smaller-img': sectionName === 'RecipeResult'}"
          alt=""
        >
        <svgicon
          v-if="sectionName === 'StoreResult'"
          :name="storeImg(item.ShortProfileName)"
          :original="true"
          :fill="false"
          class="store-img"
        />
        <figcaption>
          <h3
            v-if="item.Title"
            :class="{
              'font-text-3--bold': sectionName != 'ProductResult',
              'font-rubrik-2--black': sectionName === 'ProductResult'
            }"
            v-html="highLight(item.Title)"
          />
          <p
            v-if="item.Text && sectionName != 'RecipeResult'"
            :class="{
              'article': sectionName === 'ArticleResult' || sectionName === 'FoodSchoolResult'
            }"
            class="font-text-3--light"
            v-html="highLight(item.Text)"
          />

          <!-- Specific redering for section products -->
          <template v-if="sectionName === 'ProductResult'">
            <span class="button button--32 button--text-icon shop-online-btn">
              <svgicon
                :name="shoppingCart"
                class="search-product-shoppingcart"
              />
              Handla online
            </span>
          </template>

          <!-- Specific redering for section recipes -->
          <template v-if="sectionName === 'RecipeResult' && item.NumberOfFavorites">
            <div class="recipe-search-rating-container">
              <div class="save-recipe-container">
                <div class="save-recipe-button">
                  <svgicon
                    :name="save"
                    class="recipe-search-save"
                  />
                </div>
                <span
                  class="font-text-2--bold recipe-rating"
                  v-text="item.NumberOfFavorites"
                />
              </div>
            </div>
          </template>

          <!-- Specific redering for section stores -->
          <template v-if="sectionName === 'StoreResult'">
            <h3
              class="font-text-3--bold"
              v-html="highLight(item.MarketingName)"
            />
            <p
              class="font-text-3--light"
              v-html="highLight(item.VisitingAddress)"
            >
              <span v-html="toSentenceCase(item.VisitingCity)" />
            </p>
            <p class="font-text-3--light">Öppet {{ item.OpeningHours }}</p>
          </template>

        </figcaption>
      </figure>
    </a>
  </section>
</template>

<script>
import { dragscroll } from 'vue-dragscroll'
import ga from '@/analytics/dataLayerSearch.js'

export default {
  directives: {
    dragscroll
  },
  props: {
    sectionName: {
      type: String,
      default: ""
    },
    searchResult: {
      type: Object,
      default: () => ({})
    },
    documents: {
      type: Array,
      default: () => []
    },
    highlightItem: {
      type: Object,
      default: () => ({})
    },
    searchInput: {
      type: String,
      default: ""
    },
    position: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      save: 'Controls/Heart',
      shoppingCart: 'Shop/Shop',
      currentDragScroll: 0,
      dragComplete: true
    }
  },
  methods: {
    storeImg (profile) {
      const img = {
        supermarket: 'Logos/ICA-Supermarket',
        nara: 'Logos/ICA-Nara',
        maxi: 'Logos/ICA-Maxi-Stormarknad',
        kvantum: 'Logos/ICA-Kvantum'
      }
      return img[profile]
    },
    toSentenceCase (string) {
      return string.toUpperCase().charAt(0) + string.slice(1).toLowerCase()
    },
    hasFourImages (item) {
      return item && item.ImageUrls.length === 4
    },
    highLight (text) {
      const input = this.searchInput
      const query = new RegExp(input, 'gi') // matches uppercase and lowercase letters
      return text.replace(query, `<span class="highlighted-query">$&</span>`) || text
    },
    checkDragStatus (searchResult, searchInput, item, sectionName, position, positionInResultCategory, e) {
      if ( Math.abs(this.currentDragScroll) <= 30 ) {
        this.trackClickOnItem(searchResult, searchInput, item, sectionName, position, positionInResultCategory)
        return true
      } else {
        // As this was a dragevent, prevent the click from registering as a link-click which would close the search
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    },
    dragScrollMove (e) {
      this.currentDragScroll += Math.abs(e.detail.deltaX)
    },
    dragScrollEnd () {
      setTimeout(() => {
        this.currentDragScroll = 0
      }, 50)
    },
    trackClickOnItem (searchResult, searchInput, item, sectionName, position, positionInResultCategory) {
      ga.clickOnSearchResult(searchResult, searchInput, item, sectionName, position, positionInResultCategory)
    }
  }
}
</script>
<style lang="scss">
figcaption {
  min-width: 0;

  @ica-legacy {
    text-align: left;
  }

  h3 {
    overflow: hidden;
  }

  p {
    overflow: hidden;
    position: relative;
    max-height: 1.5rem;
    margin-right: -1em;
    padding-right: 1em;
  }

  p.article {
    max-height: 3rem;
  }

  a.services-link {
    display: inline-block;
    margin-top: 0.5rem;
  }
}

.recipes-container {
  position: relative;

  figure {
    figcaption {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      width: 100%;

      h3 {
        margin-bottom: 0.25rem;
      }

      .recipe-search-rating-container {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
      }
    }
  }

  .save-recipe-container {
    display: flex;
    align-items: center;
    margin-left: -0.25rem;

    .save-recipe-button {
      svg.recipe-search-save {
        fill: $color-inactive-text;
        margin-right: 0.125rem;
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }

  span.recipe-rating {
    color: $color-grey-dark;
    margin-top: 0.1875rem;
    letter-spacing: 0.025rem;
  }
}

.recipe-categories {
  overflow: hidden;

  .mdsa-highlight {
    display: inline-block;

    .highlight-item-image-wrapper {
      width: 5.625rem;
      min-width: 5.625rem;
      height: 5.625rem;
      position: relative;
      margin-right: 1.25rem;
    }

    figcaption {
      p {
        overflow: hidden;
        position: relative;
        max-height: 3rem;
      }
    }

    span.highlight-item-image {
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      position: absolute;
      width: 49%;
      height: 49%;

      &:nth-child(1) {
        top: 0;
        left: 0;
      }

      &:nth-child(2) {
        top: 0;
        right: 0;
      }

      &:nth-child(3) {
        bottom: 0;
        left: 0;
      }

      &:nth-child(4) {
        bottom: 0;
        right: 0;
      }
    }
  }

  .mdsa-pages-wrapper {
    margin: 0.5rem 0 1rem;
    overflow: hidden;
    width: 100%;
    white-space: nowrap;
    cursor: grabbing;

    .mdsa-page {
      margin-right: 0.5rem;
      min-width: 5.8125rem;

      &:first-child {
        margin-left: 1rem;
      }
    }
  }
}
</style>
